import { PreferenceDto, SavedSearch } from '@creditsights/api-ui-web';
import { Button, Icon, ICONS, Loader, Tooltip } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { ReactElement, RefObject, useEffect, useState } from 'react';
import useFetchRecentSearches from '../../../../hooks/preferences/useFetchRecentSearches';
import useSearchSuggestKeyboardListeners from '../../../../hooks/searchBar/useSearchSuggestKeyboardListeners';
import useFetchSavedSearches from '../../../../hooks/searches/useFetchSavedSearches';
import useFetchSearchSuggest from '../../../../hooks/searchSuggest/useFetchSearchSuggest';
import useFetchTagIdToFilterMap from '../../../../hooks/tags/useFetchTagIdToFilterMap';
import { RecentSearch } from '../../../../models/RecentSearch';
import { SearchSuggestItem } from '../../../../models/SearchSuggest';
import RecentAndSavedSuggests from '../RecentAndSavedSuggest/RecentAndSavedSuggests';
import {
  DEFAULT_RECENT_SEARCHES_COUNT,
  MAX_RECENT_SEARCHES_COUNT,
} from '../RecentAndSavedSuggest/RecentSearchesList/RecentSearchesList.constants';
import { RowSelection } from './SearchSuggest.constants';
import { getSuggestType, getTagDetailsLink, hasTagDetailsLinks } from './SearchSuggest.logic';
import styles from './SearchSuggest.module.scss';

const cx = classNames.bind(styles);

interface SearchSuggestProps {
  term: string;
  onSuggestionSelect: (suggestedTerm: string) => void;
  onSuggestionClick: (selectedTerm: string) => void;
  onLinkClick: (url: string) => void;
  dropdownRef: RefObject<HTMLDivElement>;
  navigateToSavedSearch: (savedSearch: SavedSearch) => void;
  verticalIndex: number | null;
  setVerticalIndex: React.Dispatch<React.SetStateAction<number | null>>;
}

const SearchSuggest = ({
  term,
  onSuggestionSelect,
  onSuggestionClick,
  onLinkClick,
  dropdownRef,
  navigateToSavedSearch,
  verticalIndex,
  setVerticalIndex,
}: SearchSuggestProps): ReactElement | null => {
  const { data: searchSuggest, isLoading: isSearchSuggestLoading } = useFetchSearchSuggest(term.toLowerCase());
  const {
    data: recentSearches,
    isLoading: isRecentSearchesLoading,
    isFetching: isRecentSearchesFetching,
  } = useFetchRecentSearches();
  const { data: savedSearches, isLoading: isSavedSearchesLoading } = useFetchSavedSearches();
  const tagIdToFilterMap = useFetchTagIdToFilterMap();
  const [horizontalIndex, setHorizontalIndex] = useState<0 | 1>(0);
  const [showDetailsButtons, setShowDetailsButtons] = useState(false);
  const [lastRecentSearchRemoved, setLastRecentSearchRemoved] = useState(false);
  const [mostRecentSearches, setMostRecentSearches] = useState<PreferenceDto<RecentSearch[]>>();
  const isLoading =
    isSearchSuggestLoading ||
    isRecentSearchesLoading ||
    isSavedSearchesLoading ||
    isRecentSearchesFetching ||
    lastRecentSearchRemoved;
  const maxMostRecentSearches = savedSearches?.length ? DEFAULT_RECENT_SEARCHES_COUNT : MAX_RECENT_SEARCHES_COUNT;
  const shouldHideDropdown = !isLoading && !term.length && !recentSearches?.value.length && !savedSearches?.length;

  useEffect(() => {
    if (recentSearches) {
      setMostRecentSearches({
        ...recentSearches,
        value: recentSearches?.value.slice(0, maxMostRecentSearches),
      });
    }
  }, [recentSearches]);

  const getSelectedItem = (index: number): RowSelection => {
    if (index === verticalIndex) {
      return horizontalIndex === 0 ? RowSelection.Item : RowSelection.Button;
    }
    return RowSelection.None;
  };

  useSearchSuggestKeyboardListeners({
    navigateToSavedSearch,
    onSuggestionClick,
    savedSearches,
    mostRecentSearches,
    searchSuggest,
    term,
    verticalIndex,
    setVerticalIndex,
    horizontalIndex,
    setHorizontalIndex,
  });

  // Resets selected suggestion when the term is updated
  useEffect(() => {
    setVerticalIndex(null);
  }, [term]);

  // Sets suggestion when it is selected
  useEffect(() => {
    if (!searchSuggest || verticalIndex === null || !searchSuggest[verticalIndex]?.displayName) {
      return;
    }
    onSuggestionSelect(searchSuggest[verticalIndex].displayName);
  }, [verticalIndex]);

  useEffect(() => {
    if (!searchSuggest) {
      setShowDetailsButtons(false);
      return;
    }
    const hasTagDetails = hasTagDetailsLinks(searchSuggest);
    if (hasTagDetails !== showDetailsButtons) {
      setShowDetailsButtons(hasTagDetails);
    }
  }, [searchSuggest]);

  useEffect(() => {
    setLastRecentSearchRemoved(false);
  }, [recentSearches]);

  const onLastRecentSearchRemoved = () => {
    if (!savedSearches?.length) {
      setLastRecentSearchRemoved(true);
    }
  };

  const renderDetailsButton = (suggest: SearchSuggestItem) => {
    const tagLinkDetails = getTagDetailsLink(suggest.tagId);
    if (!tagLinkDetails) {
      return null;
    }
    return (
      <Tooltip
        content={tagLinkDetails.tooltipText}
        trigger={
          <Button
            className={cx('button')}
            onClick={() => onLinkClick(tagLinkDetails.route)}
            data-testid="suggest-details-button"
          >
            <Icon name={ICONS.ArrowRight} />
          </Button>
        }
      />
    );
  };

  if (shouldHideDropdown) {
    return null;
  }

  const renderSearchSuggestOptions = () => {
    if (!term.length && (recentSearches?.value || savedSearches?.length)) {
      return (
        <RecentAndSavedSuggests
          savedSearches={savedSearches}
          verticalIndex={verticalIndex}
          navigateToSavedSearch={navigateToSavedSearch}
          onSuggestionClick={onSuggestionClick}
          allRecentSearches={recentSearches}
          onLastRecentSearchRemoved={onLastRecentSearchRemoved}
          horizontalIndex={horizontalIndex}
          recentSearches={mostRecentSearches}
          setRecentSearches={setMostRecentSearches}
        />
      );
    }

    if (searchSuggest?.length === 0) {
      return <span className={cx('no-suggestion')}>No matching suggestions</span>;
    }

    return searchSuggest?.map((suggest, index) => (
      <div
        className={cx('search-suggest-item')}
        key={`search-suggest_${suggest.tagId}`}
        data-testid="search-suggest-item"
        data-row-selection={getSelectedItem(index)}
      >
        <span
          className={cx('suggest-value-container')}
          onClick={() => onSuggestionClick(suggest.displayName)}
          data-testid="search-suggest-value-item"
        >
          <span className={cx('suggest-name')}>{suggest.displayName}</span>
          <span className={cx('suggest-tag')}>{getSuggestType(suggest.tagId, tagIdToFilterMap)}</span>
        </span>
        {showDetailsButtons && <div className={cx('right-button-container')}>{renderDetailsButton(suggest)}</div>}
      </div>
    ));
  };

  return (
    <div className={cx('body')} data-testid="search-suggest" id="search-suggest" ref={dropdownRef}>
      {isLoading ? (
        <div className={cx('loader-container')}>
          <Loader position="center" />
        </div>
      ) : (
        renderSearchSuggestOptions()
      )}
    </div>
  );
};

export default SearchSuggest;
